import { TICK_DT } from '../physics/constants';
import { Session, type Ghost, type SessionOptions } from './session';
import { Autopilot } from './autopilot';
import { solveSpeedProfile } from '../track/speedprofile';
import { dequantiseDrag, TRACE_STRIDE } from './controls';

/**
 * Re-runs a recorded trace through the same simulation that produced it.
 *
 * The trace stores the exact quantised control values the physics saw, so this
 * reproduces the original lap step for step — which is what makes both the
 * replay viewer and server-side time validation possible from one mechanism.
 *
 * `dragTrace` is an additive, optional channel: a per-tick quantised drag
 * multiplier (e.g. from a Grand Prix slipstream) recorded alongside `trace`.
 * An absent or short `dragTrace` — every trace stored before this channel
 * existed — behaves exactly as before: drag 1 on every tick it doesn't cover.
 */
export function simulateTrace(opts: SessionOptions, trace: Uint8Array, dragTrace?: Uint8Array): Session {
  const session = new Session(opts);
  session.launch();
  const ticks = Math.floor(trace.length / TRACE_STRIDE);
  for (let i = 0; i < ticks; i++) {
    // Steer is int8; the byte comes back unsigned so sign-extend it.
    const raw = trace[i * TRACE_STRIDE];
    const steer = raw > 127 ? raw - 256 : raw;
    const drag = dragTrace && i < dragTrace.length ? dequantiseDrag(dragTrace[i]) : 1;
    if (session.tick(steer, trace[i * TRACE_STRIDE + 1], drag)) break;
  }
  return session;
}

/** Total wall time a trace represents, ms. */
export function traceDuration(trace: Uint8Array): number {
  return (trace.length / TRACE_STRIDE) * TICK_DT * 1000;
}

/**
 * A ghost of the solver's own line, driven at full pace.
 *
 * Racing something genuinely faster than you teaches far more than racing your
 * own previous attempt, and this costs nothing to produce: the reference driver
 * and the solved profile both already exist.
 */
export function buildReferenceGhost(opts: SessionOptions): Ghost | null {
  const session = new Session({ ...opts, tyreModel: false });
  // Centerline profile, because the bot drives the centerline — aiming it at
  // racing-line speeds puts it in the grass.
  const centerline = solveSpeedProfile(opts.track, opts.weather.muAt, 0);
  const pilot = new Autopilot(opts.track, centerline, session.query, 0.7);

  session.launch();
  const maxTicks = Math.round(180 / TICK_DT);
  for (let i = 0; i < maxTicks; i++) {
    const { steerByte, brakeByte } = pilot.sample(session.kart);
    if (session.tick(steerByte, brakeByte)) break;
  }
  return session.result?.ghost ?? null;
}
